'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ModelForm, type ModelFormRef } from '@/components/partials/models'
import { useCreateModel } from '@/hooks'
import { CreateModelParams } from '@/types/model'
import { FormModeEnum } from '@/enums'

const CreateModel = () => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const modelFormRef = useRef<ModelFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createModel, isPending: isLoading } = useCreateModel()

  const handleSubmit = (data: CreateModelParams) => {
    createModel(data, {
      onSuccess: async newModel => {
        setIsRedirecting(true)
        queryClient.setQueryData(['model', newModel.id], newModel)
        await queryClient.invalidateQueries({ queryKey: ['models'] })
        toast.success(t('created_successfully'))
        router.push(`/products/models/${newModel.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateModelParams) => {
    createModel(data, {
      onSuccess: async newModel => {
        queryClient.setQueryData(['model', newModel.id], newModel)
        await queryClient.invalidateQueries({ queryKey: ['models'] })
        const detailUrl = `/products/models/${newModel.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        modelFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (modelFormRef.current) {
        modelFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/models')
  }

  return (
    <ModelForm
      ref={modelFormRef}
      key={formKey}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default CreateModel
